import { db } from './Database';

export interface FTLRow {
    id?: number;
    palletLpnNumberId: number;
    variant: string;
    ftlState: 'yes' | 'no';
    topMeasured?: string;
    middleMeasured?: string;
    bottomMeasured?: string;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
}

export class FtlService {
    // Get FTL details for a pallet and variant
    static async getFTLDetails(palletId: number, variant: string): Promise<{ palletLpnNumbers: any[] }> {
        try {
            // First get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                throw new Error('Pallet LPN Numbers not found');
            }

            // Get FTL rows for each LPN number
            const response = await Promise.all(palletLpnNumbers.map(async palletLpnNumber => {
                const ftlRows = await db.ftlRows
                    .where('palletLpnNumberId')
                    .equals(palletLpnNumber.id!)
                    .filter(row => row.variant === variant)
                    .toArray();

                return {
                    ...palletLpnNumber,
                    ftlRows: ftlRows || []
                };
            }));

            return {
                palletLpnNumbers: response
            };
        } catch (error) {
            console.error('Error getting FTL details:', error);
            throw error;
        }
    }

    // Create or update FTL rows
    static async createFTLRows(data: { palletId: number, palletLpnNumbers: any[] }): Promise<{ palletLpnNumbers: any[] }> {
        const { palletId, palletLpnNumbers } = data;

        return await db.transaction('rw', [db.palletLpnNumbers, db.ftlRows], async () => {
            try {
                const createdOrUpdatedFTLRows = [];
                const timestamp = new Date().toISOString();

                for (const palletLpnNumber of palletLpnNumbers) {
                    const { id, lpnNo, endStatusPallet, ftlRows, ...palletLpnNumberData } = palletLpnNumber;

                    // Create or update PalletLpnNumber
                    let palletLpnNumberRecord;
                    if (id) {
                        await db.palletLpnNumbers.update(id, {
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(id);
                    } else {
                        const newId = await db.palletLpnNumbers.add({
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(newId);
                    }

                    if (!palletLpnNumberRecord) continue;

                    // Create or update FTL rows
                    const ftlRowsArray = [];
                    for (const ftlRow of ftlRows) {
                        const { id, ...ftlRowData } = ftlRow;

                        if (id) {
                            await db.ftlRows.update(id, {
                                ...ftlRowData,
                                updatedAt: timestamp
                            });
                            const updated = await db.ftlRows.get(id);
                            if (updated) ftlRowsArray.push(updated);
                        } else {
                            const newId = await db.ftlRows.add({
                                ...ftlRowData,
                                palletLpnNumberId: palletLpnNumberRecord.id,
                                createdAt: timestamp,
                                updatedAt: timestamp
                            });
                            const created = await db.ftlRows.get(newId);
                            if (created) ftlRowsArray.push(created);
                        }
                    }

                    createdOrUpdatedFTLRows.push({
                        ...palletLpnNumberRecord,
                        ftlRows: ftlRowsArray
                    });
                }

                return {
                    palletLpnNumbers: createdOrUpdatedFTLRows
                };
            } catch (error) {
                console.error('Error creating/updating FTL rows:', error);
                throw error;
            }
        });
    }
}
